import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Badge, Input, Separator, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuCheckboxItem, } from '@cargomatic/ui';
import { PlusCircle, Search } from 'lucide-react';
import { cn } from 'utils/ui/classes';
import LocationWebAPIUtils from 'utils/LocationWebAPIUtils';
export function DataTableTextFilter({ column, title, className }) {
    var _a;
    const [open, setOpen] = useState(false);
    const filterValues = (_a = column === null || column === void 0 ? void 0 : column.getFilterValue()) !== null && _a !== void 0 ? _a : [];
    const selectedValues = useMemo(() => new Set(filterValues), [filterValues]);
    const [searchText, setSearchText] = useState('');
    const [customZones, setCustomZones] = useState([]);
    const inputRef = useRef();
    const customZoneNames = useMemo(() => {
        return (customZones !== null && customZones !== void 0 ? customZones : []).map((zone) => zone.name);
    }, [customZones]);
    async function fetchCustomZones() {
        const response = await LocationWebAPIUtils.getCustomZones();
        setCustomZones(response.zones);
    }
    useEffect(() => {
        fetchCustomZones();
    }, []);
    const selectedValuesCount = useMemo(() => filterValues.length, [filterValues]);
    const remainingValues = useMemo(() => {
        return filterValues.filter((val) => !customZoneNames.includes(val));
    }, [filterValues, customZoneNames]);
    function handleOpenChange(open) {
        if (open) {
            setTimeout(() => {
                var _a;
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100);
        }
    }
    function handleSearchTextChange(e) {
        const { target: { value }, } = e;
        setSearchText(value);
    }
    function handleSearch() {
        column === null || column === void 0 ? void 0 : column.setFilterValue([...filterValues, searchText]);
        setSearchText('');
        hide();
    }
    function handleToggleZoneSelect(zoneName, checked) {
        if (!checked) {
            handleRemove(zoneName);
        }
        else {
            column === null || column === void 0 ? void 0 : column.setFilterValue([...filterValues, zoneName]);
            setSearchText('');
        }
        hide();
    }
    function handleRemove(value) {
        column === null || column === void 0 ? void 0 : column.setFilterValue(filterValues.filter((text) => text !== value));
        hide();
    }
    function handleReset() {
        setSearchText('');
        column.setFilterValue(undefined);
        hide();
    }
    function handleSearchKeyDown(e) {
        if (e.key === 'Enter') {
            handleSearch();
        }
        e.stopPropagation();
    }
    function hide() {
        setOpen(false);
    }
    return (React.createElement(DropdownMenu, { modal: false, onOpenChange: handleOpenChange, open: open },
        React.createElement(DropdownMenuTrigger, { asChild: true },
            React.createElement(Button, { variant: "outline", size: "sm", className: "border-dashed flex items-center", onClick: () => setOpen(true) },
                React.createElement(PlusCircle, { className: "mr-2 h-4 w-4" }),
                title,
                selectedValuesCount > 0 && (React.createElement(React.Fragment, null,
                    React.createElement(Separator, { orientation: "vertical", className: "mx-2 h-4" }),
                    React.createElement("div", { className: "flex items-center space-x-2" }, filterValues.map((value) => (React.createElement(Badge, { className: "rounded-sm px-1 font-normal h-4 flex justify-between items-center" },
                        React.createElement("span", null, value))))))))),
        React.createElement(DropdownMenuContent, { onCloseAutoFocus: (e) => e.preventDefault(), onInteractOutside: hide, onFocusOutside: hide, onEscapeKeyDown: hide, onPointerDownOutside: hide, className: cn('p-0 overflow-y-auto', className), align: "start", forceMount: true },
            React.createElement("div", { className: "flex w-full max-w-sm items-center space-x-1 px-2 py-1.5" }),
            React.createElement("div", { className: "flex w-full max-w-sm items-center space-x-1 px-2 py-1.5" },
                React.createElement(Input, { type: "search", placeholder: `Search ${title}`, onChange: handleSearchTextChange, onKeyDown: handleSearchKeyDown, className: "flex-grow-0 flex-shrink-0j", value: searchText, ref: inputRef }),
                React.createElement(Button, { type: "submit", variant: "icon", size: "icon", onClick: handleSearch, disabled: searchText.length === 0 },
                    React.createElement(Search, { className: "w-3 h-3" }))),
            React.createElement(DropdownMenuSeparator, null),
            customZones.map((zone) => (React.createElement(DropdownMenuCheckboxItem, { key: zone.id, onCheckedChange: (checked) => handleToggleZoneSelect(zone.name, checked), checked: selectedValues.has(zone.name) },
                React.createElement("span", null, zone.name)))),
            remainingValues.map((value) => (React.createElement(React.Fragment, null,
                React.createElement(DropdownMenuCheckboxItem, { key: value, onCheckedChange: () => handleRemove(value), checked: true },
                    React.createElement("span", null, value))))),
            React.createElement(DropdownMenuItem, { onClick: handleReset, className: "pl-8" }, "Clear"))));
}
